import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import API_BASE_URL from "../config";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
    FiRefreshCw,
    FiSearch,
    FiClock,
    FiTrash2,
    FiAlertCircle,
    FiMessageCircle,
    FiCheckCircle,
    FiDownload,
    FiUser,
    FiMail
} from "react-icons/fi";
import {
    MdOutlineMessage,
    MdOutlinePendingActions,
    MdOutlineCheckCircleOutline,
    MdOutlineDeleteOutline
} from "react-icons/md";
import "../styles/AdminUserComplaints.css";

export default function AdminUserComplaints() {
    const { token } = useAuth();

    const [complaints, setComplaints] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [exportLoading, setExportLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("ALL");

    const [stats, setStats] = useState({
        total: 0,
        new: 0,
        resolved: 0
    });

    /* ======================
       FETCH COMPLAINTS
    ====================== */
    const fetchComplaints = async () => {
        try {
            setLoading(true);

            const queryParams = new URLSearchParams({
                tab: activeTab,
                ...(searchTerm && { search: searchTerm })
            }).toString();

            const res = await fetch(
                `${API_BASE_URL}/api/contact?${queryParams}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            const data = await res.json();

            if (data.success) {
                const list = data.data || [];
                setComplaints(list);
            } else {
                toast.error("Failed to load complaints");
            }
        } catch (error) {
            console.error("Fetch complaints error:", error);
            toast.error("Failed to load complaints");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchComplaints();
        fetchStats();
    }, [activeTab, searchTerm]);

    /* ======================
       EXPORT TO EXCEL
    ====================== */
    const handleExport = async () => {
        try {
            setExportLoading(true);

            const params = new URLSearchParams({
                tab: activeTab,
                search: searchTerm,
                export: "excel"
            }).toString();

            const res = await fetch(
                `${API_BASE_URL}/api/contact/export/excel?${params}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (!res.ok) throw new Error("Export failed");

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = `user_complaints_${Date.now()}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();

            toast.success("Excel downloaded successfully");
        } catch (err) {
            console.error("Export error:", err);
            toast.error("Excel export failed");
        } finally {
            setExportLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const res = await fetch(
                `${API_BASE_URL}/api/contact?tab=ALL`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            const data = await res.json();

            if (data.success) {
                const allList = data.data || [];

                const total = allList.length;
                const newCount = allList.filter(c => c.status === "NEW").length;
                const resolvedCount = allList.filter(c => c.status === "RESOLVED").length;

                setStats({
                    total,
                    new: newCount,
                    resolved: resolvedCount
                });
            }
        } catch (err) {
            console.error("Stats fetch error:", err);
        }
    };


    /* ======================
       MARK AS RESOLVED
    ====================== */
    const handleMarkResolved = async (complaint) => {
        const { value: reply } = await Swal.fire({
            title: "Resolve Complaint",
            html: `
      <div style="text-align:left;">
        <p style="margin-bottom:8px;"><strong>Subject:</strong> ${complaint.subject}</p>
        <p style="margin-bottom:12px;"><strong>User:</strong> ${complaint.name} (${complaint.email})</p>

        <label style="display:block; margin-bottom:6px; font-weight:600;">
          Admin Reply
        </label>
        <textarea 
          id="adminReply"
          class="swal2-textarea"
          placeholder="Enter resolution reply for this complaint..."
          style="width:90%; min-height:120px;"
        ></textarea>
      </div>
    `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: "Resolve & Save Reply",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#10b981",
            preConfirm: () => {
                const replyText = document.getElementById("adminReply").value;
                if (!replyText || replyText.trim().length < 5) {
                    Swal.showValidationMessage("Reply is required (minimum 5 characters)");
                    return false;
                }
                return replyText;
            },
            width: "600px"
        });

        if (!reply) return;

        try {
            const res = await fetch(
                `${API_BASE_URL}/api/contact/${complaint._id}/resolve`,
                {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        reply,
                        role: "ADMIN"
                    })
                }
            );

            const data = await res.json();

            if (data.success) {
                toast.success("Complaint resolved and feedback saved");
                fetchComplaints();
            } else {
                toast.error(data.message || "Resolve failed");
            }
        } catch (err) {
            console.error("Resolve error:", err);
            toast.error("Failed to resolve complaint");
        }
    };

    /* ======================
       DELETE COMPLAINT
    ====================== */
    const handleDeleteComplaint = async (id) => {
        const result = await Swal.fire({
            title: "Delete Complaint?",
            html: `
        <div style="text-align: left; padding: 10px 0;">
          <p>This action <strong>cannot be undone</strong>.</p>
          <p style="color: #6b7280; font-size: 14px; margin-top: 8px;">
            All associated data will be permanently removed.
          </p>
        </div>
      `,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#dc2626",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Delete Permanently",
            cancelButtonText: "Cancel",
            width: "500px"
        });

        if (!result.isConfirmed) return;

        try {
            const res = await fetch(`${API_BASE_URL}/api/contact/${id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` }
            });

            const data = await res.json();

            if (data.success) {
                toast.success("Complaint deleted successfully");
                fetchComplaints();
            } else {
                toast.error(data.message || "Delete failed");
            }
        } catch (err) {
            console.error("Delete error:", err);
            toast.error("Failed to delete complaint");
        }
    };

    /* ======================
       VIEW COMPLAINT DETAILS
    ====================== */
    const handleViewComplaintDetails = (complaint) => {
        Swal.fire({
            title: "Complaint Details",
            html: `
    <div style="text-align: left; line-height: 1.8; padding: 10px 0;">

      <!-- SUBJECT -->
      <div style="margin-bottom: 15px;">
        <strong style="color: #374151;">Subject:</strong>
        <div style="color: #111827; font-size: 16px; font-weight: 600; margin-top: 4px;">
          ${complaint.subject || "No Subject"}
        </div>
      </div>

      <!-- USER INFO -->
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
        <div>
          <strong style="color: #374151;">User Name:</strong>
          <div style="color: #111827; margin-top: 4px;">${complaint.name || "-"}</div>
        </div>
        <div>
          <strong style="color: #374151;">Email:</strong>
          <div style="color: #111827; margin-top: 4px;">${complaint.email || "-"}</div>
        </div>
      </div>

      <!-- USER MESSAGE -->
      <div style="margin-bottom: 15px;">
        <strong style="color: #374151;">Message:</strong>
        <div style="color: #111827; background: #f9fafb; padding: 12px; border-radius: 8px; margin-top: 8px; border: 1px solid #e5e7eb;">
          ${complaint.message || "No message provided"}
        </div>
      </div>

      <!-- STATUS & DATE -->
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
        <div>
          <strong style="color: #374151;">Status:</strong>
          <div style="margin-top: 4px;">
            <span style="
              display: inline-flex;
              align-items: center;
              gap: 4px;
              padding: 4px 12px;
              border-radius: 20px;
              font-size: 13px;
              font-weight: 600;
              background-color: ${complaint.status === 'NEW' ? '#fef3c7' : '#d1fae5'};
              color: ${complaint.status === 'NEW' ? '#92400e' : '#065f46'};
            ">
              ${complaint.status === 'NEW' ? 'New' : 'Resolved'}
            </span>
          </div>
        </div>

        <div>
          <strong style="color: #374151;">Submitted:</strong>
          <div style="color: #111827; margin-top: 4px;">
            ${new Date(complaint.createdAt).toLocaleDateString()} 
            ${new Date(complaint.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </div>

      ${complaint.status === "RESOLVED"
                    ? `
          <!-- ADMIN REPLY SECTION -->
          <div style="margin-top: 20px;">
            <strong style="color: #065f46;">Admin Reply:</strong>
            <div style="
              background: #ecfdf5;
              padding: 12px;
              border-radius: 8px;
              margin-top: 8px;
              border: 1px solid #a7f3d0;
              color: #064e3b;
              white-space: pre-wrap;
            ">
              ${complaint.adminReply || "No reply recorded"}
            </div>

            <div style="margin-top: 10px; font-size: 13px; color: #6b7280;">
              Resolved At: ${complaint.resolvedAt
                        ? new Date(complaint.resolvedAt).toLocaleString()
                        : "-"
                    }
            </div>
          </div>
          `
                    : ""
                }

    </div>
    `,
            icon: "info",
            confirmButtonText: "Close",
            confirmButtonColor: "#111827",
            width: "650px"
        });
    };


    /* ======================
       RENDER STATUS BADGE
    ====================== */
    const renderStatusBadge = (status) => {
        const finalStatus = status || "NEW";

        const statusConfig = {
            "NEW": {
                color: "#f59e0b",
                icon: <MdOutlinePendingActions size={14} />,
                label: "New"
            },
            "RESOLVED": {
                color: "#10b981",
                icon: <MdOutlineCheckCircleOutline size={14} />,
                label: "Resolved"
            }
        };

        const config = statusConfig[finalStatus] || {
            color: "#6b7280",
            icon: <FiAlertCircle size={14} />,
            label: "Unknown"
        };

        return (
            <span
                className="admin-complaints-status-badge"
                style={{
                    backgroundColor: `${config.color}15`,
                    color: config.color
                }}
            >
                {config.icon}
                <span>{config.label}</span>
            </span>
        );
    };

    /* ======================
       HEADER STATS COMPONENT
    ====================== */
    const renderHeaderStats = () => (
        <div className="admin-complaints-stats-grid">
            <div className="admin-complaints-stat-card admin-complaints-stat-total">
                <div className="admin-complaints-stat-icon">
                    <MdOutlineMessage size={24} />
                </div>
                <div className="admin-complaints-stat-content">
                    <h3 className="admin-complaints-stat-value">{stats.total}</h3>
                    <p className="admin-complaints-stat-label">Total Complaints</p>
                </div>
            </div>

            <div className="admin-complaints-stat-card admin-complaints-stat-new">
                <div className="admin-complaints-stat-icon">
                    <MdOutlinePendingActions size={24} />
                </div>
                <div className="admin-complaints-stat-content">
                    <h3 className="admin-complaints-stat-value">{stats.new}</h3>
                    <p className="admin-complaints-stat-label">New</p>
                    <span className="admin-complaints-stat-subtext">Pending Resolution</span>
                </div>
            </div>

            <div className="admin-complaints-stat-card admin-complaints-stat-resolved">
                <div className="admin-complaints-stat-icon">
                    <MdOutlineCheckCircleOutline size={24} />
                </div>
                <div className="admin-complaints-stat-content">
                    <h3 className="admin-complaints-stat-value">{stats.resolved}</h3>
                    <p className="admin-complaints-stat-label">Resolved</p>
                    <span className="admin-complaints-stat-subtext">Issues Fixed</span>
                </div>
            </div>
        </div>
    );

    return (
        <div className="premium-admin-complaints">
            {/* ======================
         HEADER
      ====================== */}
            <div className="admin-complaints-header">
                <div className="admin-complaints-header-left">
                    <h1 className="admin-complaints-page-title">User Complaints</h1>
                    <p className="admin-complaints-page-subtitle">
                        Manage and resolve all user complaints and feedback
                    </p>
                </div>
                <div className="admin-complaints-header-right">
                    <button
                        className="admin-complaints-refresh-btn"
                        onClick={fetchComplaints}
                        disabled={loading}
                    >
                        <FiRefreshCw size={18} />
                        <span>{loading ? "Refreshing..." : "Refresh"}</span>
                    </button>
                    <button
                        className="admin-complaints-export-btn"
                        onClick={handleExport}
                        disabled={exportLoading || complaints.length === 0}
                        title={complaints.length === 0 ? "No data to export" : "Export to Excel"}
                    >
                        {exportLoading ? (
                            <>
                                <div className="admin-complaints-export-spinner"></div>
                                <span>Exporting...</span>
                            </>
                        ) : (
                            <>
                                <FiDownload size={18} />
                                <span style={{ color: "white" }}>Export ({complaints.length})</span>
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* ======================
         STATS
      ====================== */}
            {renderHeaderStats()}

            {/* ======================
         MAIN CONTENT
      ====================== */}
            <div className="admin-complaints-content">
                {/* Controls */}
                <div className="admin-complaints-controls">
                    <div className="admin-complaints-tabs-container">
                        {["ALL", "NEW", "RESOLVED"].map(tab => (
                            <button
                                key={tab}
                                className={`admin-complaints-tab-btn ${activeTab === tab ? "admin-complaints-tab-active" : ""}`}
                                onClick={() => setActiveTab(tab)}
                            >
                                {tab === "NEW" && <MdOutlinePendingActions size={16} />}
                                {tab === "RESOLVED" && <FiCheckCircle size={16} />}
                                {tab === "ALL" && <MdOutlineMessage size={16} />}
                                <span>
                                    {tab === "ALL" && `All (${stats.total})`}
                                    {tab === "NEW" && `New (${stats.new})`}
                                    {tab === "RESOLVED" && `Resolved (${stats.resolved})`}
                                </span>
                            </button>
                        ))}
                    </div>

                    <div className="admin-complaints-controls-right">
                        <div className="admin-complaints-search-box">
                            <FiSearch className="admin-complaints-search-icon" />
                            <input
                                type="text"
                                placeholder="Search by name, email, subject, message..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="admin-complaints-search-input"
                            />
                        </div>
                    </div>
                </div>

                {/* ======================
           TABLE
        ====================== */}
                <div className="admin-complaints-table-container">
                    {loading ? (
                        <div className="admin-complaints-loading-state">
                            <div className="admin-complaints-loading-spinner"></div>
                            <p>Loading complaints...</p>
                        </div>
                    ) : (
                        <table className="admin-complaints-data-table">
                            <thead>
                                <tr>
                                    <th>User Information</th>
                                    <th>Email Address</th>
                                    <th>Subject</th>
                                    <th>Submitted Date</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                {complaints.length > 0 ? (
                                    complaints.map(complaint => (
                                        <tr key={complaint._id} className="admin-complaints-data-row">
                                            <td>
                                                <div className="admin-complaints-user-info">
                                                    <div className="admin-complaints-user-avatar">
                                                        {complaint.accountRole === "USER" && complaint.userProfile?.photo ? (
                                                            <img
                                                                src={complaint.userProfile.photo}
                                                                alt={complaint.name}
                                                                className="admin-complaints-avatar-img"
                                                            />
                                                        ) : complaint.accountRole === "SOLICITOR" && complaint.solicitorProfile?.photo ? (
                                                            <img
                                                                src={complaint.solicitorProfile.photo}
                                                                alt={complaint.name}
                                                                className="admin-complaints-avatar-img"
                                                            />
                                                        ) : (
                                                            <div className="avatar-fallback">
                                                                {complaint.name?.charAt(0)?.toUpperCase() || "U"}
                                                            </div>
                                                        )}
                                                    </div>


                                                    <div className="admin-complaints-user-details">
                                                        <div className="admin-complaints-user-name">
                                                            {complaint.name || "Anonymous"}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>

                                            <td>
                                                <div className="admin-complaints-email-cell">
                                                    <div className="admin-complaints-email">
                                                        <FiMail size={12} />
                                                        <span>{complaint.email || "No email"}</span>
                                                    </div>
                                                </div>
                                            </td>

                                            <td>
                                                <div className="admin-complaints-subject-cell">
                                                    <div className="admin-complaints-subject">
                                                        <strong>{complaint.subject || "No Subject"}</strong>
                                                    </div>
                                                    <div className="admin-complaints-message-preview">
                                                        <small>
                                                            {complaint.message?.substring(0, 60) || "No message..."}
                                                            {complaint.message?.length > 60 ? "..." : ""}
                                                        </small>
                                                    </div>
                                                </div>
                                            </td>

                                            <td>
                                                <div className="admin-complaints-date-cell">
                                                    <div className="admin-complaints-date">
                                                        {new Date(complaint.createdAt).toLocaleDateString()}
                                                    </div>
                                                    <div className="admin-complaints-time">
                                                        <span>
                                                            {new Date(complaint.createdAt).toLocaleTimeString([], {
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>

                                            <td>
                                                {renderStatusBadge(complaint.status)}
                                            </td>

                                            <td>
                                                <div className="admin-complaints-action-buttons">
                                                    <button
                                                        className="admin-complaints-view-btn"
                                                        onClick={() => handleViewComplaintDetails(complaint)}
                                                        title="View Complaint Details"
                                                    >
                                                        View Details
                                                    </button>

                                                    {complaint.status !== "RESOLVED" && (
                                                        <button
                                                            className="admin-complaints-resolve-btn"
                                                            onClick={() => handleMarkResolved(complaint)}
                                                        >
                                                            Resolve
                                                        </button>
                                                    )}

                                                    <button
                                                        className="admin-complaints-delete-btn"
                                                        onClick={() => handleDeleteComplaint(complaint._id)}
                                                        title="Delete Complaint"
                                                    >
                                                        <MdOutlineDeleteOutline size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr className="admin-complaints-no-data-row">
                                        <td colSpan="6">
                                            <div className="admin-complaints-no-data">
                                                <MdOutlineMessage size={48} />
                                                <p>No complaints found</p>
                                                <small>Try adjusting your search or filters</small>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}